import { pick, compact } from "lodash";

/*

JSON API UTILS

  To better deal with the JSON API specification, we can normalize records using
our own normalization spec detailed here.

* ID, attributes, links, and relationship data exist un-nested at the top level
* If an object from a relationship is included in the response (and passed to one of the normalizeApiResponse functions),
  the included object will be merged into the normalized object.
* A metadata object is added as the '_meta' property [1]

---
1. Metadata is required for tasks like extracting back out just the original
attributes for sending to an API.

*/

/**
 * Normalizes an entire JSON API response with one top-level record
 * @param response
 */
export function normalizeApiResponse(
  response: IJsonApiResponse
): INormalizedObject {
  return normalizeRecord(response.data, response.included);
}

/**
 * Normalizes an entire JSON API response with an array of records
 * @param response
 */
export function normalizeApiArrayResponse(
  response: IJsonApiResponse
): INormalizedObject[] {
  return normalizeArray(response.data, response.included);
}

type NormalizationOptions = {
  includeDepth?: number;
  currentIncludeDepth?: number;
};
/**
 * Normalizes a JSON API record
 * @param record
 * @param includedRecords - Included records from a JSON API response
 * @param options - Allows options such as relationship depth
 */
export function normalizeRecord<T>(
  record: IJsonApiRecord,
  includedRecords?: IJsonApiRecord[],
  options: NormalizationOptions = {}
) {
  const { includeDepth, currentIncludeDepth } = options;
  const attributeNames = Object.keys(record.attributes);
  let normalized: Dictionary = {
    id: record.id,
    ...record.attributes,
    ...extractRelationships(
      record,
      includedRecords,
      includeDepth,
      currentIncludeDepth
    ),
    _meta: { attributeNames },
  };
  if (record.links) {
    normalized.links = record.links;
  }
  return normalized as T;
}

/**
 * Normalizes an array of JSON API records
 * @param records
 * @param includedRecords
 */
export function normalizeArray<T>(
  records: IJsonApiRecord[],
  includedRecords?: IJsonApiRecord[]
) {
  return records.map(r => normalizeRecord<T>(r, includedRecords));
}

/**
 * Extracts just the original attributes from a normalized object using metadata
 * @param normalizedObject
 */
export function getAttributes(normalizedObject) {
  if (!normalizedObject._meta) {
    throw new TypeError(
      "Object passed to getAttributes has no _meta property. Ensure it is a normalized API record."
    );
  }
  return pick(normalizedObject, normalizedObject._meta.attributeNames);
}

/**
 * Extracts relationship data from a JSON API record
 * @param record
 * @param includedRecords
 * @param depth - depth to extract records to
 * @param currentDepth - used for recursive processing of relationships
 */
export function extractRelationships(
  record: IJsonApiRecord,
  includedRecords: IJsonApiRecord[],
  depth = 2,
  currentDepth: number = 1
) {
  if (!record.relationships || currentDepth > depth) {
    return {};
  } else {
    let obj = {};

    Object.keys(record.relationships).forEach(rel => {
      const data = record.relationships[rel].data;
      let result = data;

      if (data && includedRecords) {
        if (Array.isArray(data)) {
          result = compact(
            data.map(item => {
              const includedItem = includedRecords.find(
                inc => inc.id === item.id && inc.type === item.type
              );
              return includedItem
                ? normalizeRecord(includedItem, includedRecords, {
                    includeDepth: depth,
                    currentIncludeDepth: currentDepth + 1,
                  })
                : null;
            })
          );
        } else {
          const include = includedRecords.find(
            inc => inc.id === data.id && inc.type === data.type
          );
          if (include) {
            result = normalizeRecord(include, includedRecords, {
              includeDepth: depth,
              currentIncludeDepth: currentDepth + 1,
            });
          }
        }
      }

      obj[rel] = result;
    });

    return obj;
  }
}



// WEBPACK FOOTER //
// ./app/javascript/libs/api_utils.ts